import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MembershipCard = ({ currentTier, onUpgrade, onManageBilling }) => {
  const membershipTiers = {
    free: {
      name: 'Free Collector',
      price: '$0',
      period: 'forever',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted/20',
      borderColor: 'border-border',
      features: [
        'Track up to 50 items',
        'Basic market data',
        'Community access',
        'Mobile app access'
      ],
      limitations: [
        'Limited to 50 items',
        'Basic analytics only',
        'No export features',
        'Standard support'
      ]
    },
    premium: {
      name: 'Premium Collector',
      price: '$9.99',
      period: 'per month',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      features: [
        'Unlimited item tracking',
        'Advanced market analytics',
        'Price alerts & notifications',
        'Data export & backup',
        'Priority support',
        'Advanced portfolio insights'
      ],
      limitations: []
    },
    elite: {
      name: 'Elite Collector',
      price: '$24.99',
      period: 'per month',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      features: [
        'Everything in Premium',
        'AI-powered valuations',
        'Auction integration',
        'Professional reports',
        'White-glove support',
        'Early access to features',
        'Custom collection sharing',
        'Advanced authentication tools'
      ],
      limitations: []
    }
  };

  const current = membershipTiers?.[currentTier];
  const nextTier = currentTier === 'free' ? 'premium' : currentTier === 'premium' ? 'elite' : null;
  const next = nextTier ? membershipTiers?.[nextTier] : null;

  const benefits = {
    premium: [
      { icon: 'Infinity', text: 'Unlimited item tracking' },
      { icon: 'TrendingUp', text: 'Advanced analytics' },
      { icon: 'Bell', text: 'Price alerts' },
      { icon: 'Download', text: 'Data export' }
    ],
    elite: [
      { icon: 'Brain', text: 'AI valuations' },
      { icon: 'Gavel', text: 'Auction integration' },
      { icon: 'FileText', text: 'Professional reports' },
      { icon: 'Crown', text: 'VIP support' }
    ]
  };

  return (
    <div className="space-y-6">
      {/* Current Membership */}
      <div className={`bg-card border ${current?.borderColor} rounded-xl p-6 premium-hover`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${current?.bgColor}`}>
              <Icon name="Crown" size={24} className={current?.color} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Current Plan</h2>
              <p className="text-muted-foreground">Your active membership</p>
            </div>
          </div>
          {currentTier !== 'free' && (
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              onClick={onManageBilling}
            >
              Manage Billing
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-baseline space-x-2">
            <span className={`text-3xl font-bold ${current?.color}`}>{current?.name}</span>
            <div className="text-muted-foreground">
              <span className="text-2xl font-bold text-foreground">{current?.price}</span>
              {current?.period !== 'forever' && <span className="text-sm">/{current?.period}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-foreground mb-2">Included Features</h4>
              <ul className="space-y-2">
                {current?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {current?.limitations?.length > 0 && (
              <div>
                <h4 className="font-semibold text-foreground mb-2">Limitations</h4>
                <ul className="space-y-2">
                  {current?.limitations?.map((limitation, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm">
                      <Icon name="X" size={16} className="text-muted-foreground" />
                      <span className="text-muted-foreground">{limitation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Upgrade Options */}
      {next && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="ArrowUp" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Upgrade Available</h3>
              <p className="text-muted-foreground">Unlock more powerful features</p>
            </div>
          </div>

          <div className={`border ${next?.borderColor} rounded-lg p-4 mb-4`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-baseline space-x-2">
                <span className={`text-xl font-bold ${next?.color}`}>{next?.name}</span>
                <div className="text-muted-foreground">
                  <span className="text-lg font-bold text-foreground">{next?.price}</span>
                  <span className="text-sm">/{next?.period}</span>
                </div>
              </div>
              <Button
                variant="default"
                iconName="Crown"
                iconPosition="left"
                onClick={() => onUpgrade(nextTier)}
                className="liquid-gold"
              >
                Upgrade Now
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {benefits?.[nextTier]?.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name={benefit?.icon} size={16} className={next?.color} />
                  <span className="text-sm text-muted-foreground">{benefit?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="bg-muted/20 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-3">What you'll get with {next?.name}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {next?.features?.slice(current?.features?.length)?.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="Plus" size={16} className="text-success" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* All Plans Comparison */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Compare All Plans</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 text-foreground">Feature</th>
                <th className="text-center py-3 text-muted-foreground">Free</th>
                <th className="text-center py-3 text-primary">Premium</th>
                <th className="text-center py-3 text-warning">Elite</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <tr className="border-b border-border/50">
                <td className="py-3 text-muted-foreground">Item Tracking</td>
                <td className="text-center py-3">50 items</td>
                <td className="text-center py-3">Unlimited</td>
                <td className="text-center py-3">Unlimited</td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-3 text-muted-foreground">Market Data</td>
                <td className="text-center py-3">Basic</td>
                <td className="text-center py-3">Advanced</td>
                <td className="text-center py-3">AI-Powered</td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-3 text-muted-foreground">Price Alerts</td>
                <td className="text-center py-3"><Icon name="X" size={16} className="text-muted-foreground mx-auto" /></td>
                <td className="text-center py-3"><Icon name="Check" size={16} className="text-success mx-auto" /></td>
                <td className="text-center py-3"><Icon name="Check" size={16} className="text-success mx-auto" /></td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-3 text-muted-foreground">Data Export</td>
                <td className="text-center py-3"><Icon name="X" size={16} className="text-muted-foreground mx-auto" /></td>
                <td className="text-center py-3"><Icon name="Check" size={16} className="text-success mx-auto" /></td>
                <td className="text-center py-3"><Icon name="Check" size={16} className="text-success mx-auto" /></td>
              </tr>
              <tr>
                <td className="py-3 text-muted-foreground">Support</td>
                <td className="text-center py-3">Standard</td>
                <td className="text-center py-3">Priority</td>
                <td className="text-center py-3">White-glove</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MembershipCard;